import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import classNames from 'classnames';
import * as React from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useRmsGanttValue } from '../../RmsContext';
import {
    GanttGroupNameBar,
    StyledBox,
    StyledCollapseChildren,
    StyledCountBox,
    transitionDuration,
    transitionEasing,
} from './CollapsibleGanttGroup.style';
import { collapsedGanttGroupHeight, GanttGroup, getGroupHeight } from './GanttGroup.style';

export const CollapsibleGanttGroup = memo(
    ({
        index,
        resourceName,
        groupNamelabel,
        nbRows,
        nbTurnarounds,
        children,
        firstOrLast,
    }: {
        index: number;
        groupNamelabel: React.ReactNode;
        nbRows: number;
        nbTurnarounds: number;
        children?: React.ReactNode;
        resourceName?: string;
        firstOrLast: boolean;
    }) => {
        const resourcesByType = useRmsGanttValue('resourcesByType');
        const [open, setOpen] = React.useState(true);
        const [vh, setVh] = useState<number>(
            Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0),
        );
        const handleClick = useCallback(() => setOpen(!open), [open]);

        const handleResize = useCallback(() => {
            setVh(Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0));
        }, []);
        useEffect(() => {
            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }, [handleResize]);

        return (
            <StyledBox id={`${resourceName}-group`} className={classNames({ firstOrLast })}>
                {/*sticky*/}
                <GanttGroupNameBar
                    index={index}
                    open={open}
                    resource={
                        resourceName === '' || resourcesByType?.find(r => r.name === resourceName)?.active
                            ? 'true'
                            : 'false'
                    }
                    groupheight={getGroupHeight(vh, nbRows)}
                    onClick={handleClick}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-end',
                            width: '55px',
                        }}
                    >
                        <Box sx={{ width: '34px', display: 'flex', justifyContent: 'center' }}>{groupNamelabel}</Box>
                        {open ? (
                            <KeyboardArrowUp width={10} height={10} />
                        ) : (
                            <KeyboardArrowDown width={10} height={10} />
                        )}
                    </Box>
                </GanttGroupNameBar>

                <GanttGroup
                    groupheight={getGroupHeight(vh, nbRows)}
                    sx={[
                        {
                            backdropFilter: index % 2 === 0 ? undefined : 'brightness(1.15)',
                            backgroundColor: open
                                ? undefined
                                : theme =>
                                      index % 2 === 0 ? theme.palette.background.default : theme.palette.secondary.main,
                            transition: `background ${transitionDuration}, height ${transitionDuration} ${transitionEasing}`,
                        },
                        !open && { height: collapsedGanttGroupHeight, cursor: 'pointer' },
                    ]}
                >
                    {/* sticky */}
                    <StyledCountBox index={index} sx={{ opacity: open ? 0 : 1 }}>
                        <Typography component={'span'} variant={'h5'}>
                            <FormattedMessage id={'rms.collapsedTurnarounds'} values={{ turnarounds: nbTurnarounds }} />
                        </Typography>
                    </StyledCountBox>

                    <StyledCollapseChildren open={open} groupheight={getGroupHeight(vh, nbRows)}>
                        {children}
                    </StyledCollapseChildren>
                </GanttGroup>
            </StyledBox>
        );
    },
);
